import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * The record an operator arrived here to look at.
 *
 * The ops-alert app links out to this dashboard, but the dashboard has no
 * per-record routes — detail lives in in-page state and modals, not URLs. So a
 * push about one failed withdrawal used to drop the operator on the full
 * transactions list to find it themselves, which is most of the value of the
 * alert gone.
 *
 * The id rides in `?focus=`, and a page uses it to open, highlight and scroll
 * to the row. This is the closest thing to a deep link the current routing
 * allows, and it stops being a workaround the day these pages get real detail
 * routes — the parameter simply becomes redundant.
 */
export interface FocusedRecord {
  /** The id to open, or null. */
  focusId: string | null;
  /** True until the page reports whether it found the record. */
  isPending: boolean;
  /** Call when the record is on screen — clears the parameter from the URL. */
  resolveFocus: () => void;
  /** Call when the loaded page does not contain it. */
  missFocus: () => void;
  /** Set once the page has looked and come up empty. */
  notFound: boolean;
  /** Dismisses the not-found notice and drops the parameter. */
  dismissFocus: () => void;
}

export const useFocusedRecord = (): FocusedRecord => {
  const location = useLocation();
  const navigate = useNavigate();

  const focusId = new URLSearchParams(location.search).get("focus");
  const [notFound, setNotFound] = useState(false);
  const [isPending, setIsPending] = useState(Boolean(focusId));

  // A new focus id means a fresh look, even without a remount.
  const lastSeen = useRef<string | null>(focusId);
  useEffect(() => {
    if (lastSeen.current !== focusId) {
      lastSeen.current = focusId;
      setNotFound(false);
      setIsPending(Boolean(focusId));
    }
  }, [focusId]);

  /**
   * Strips `?focus=` once the record is on screen.
   *
   * `replace` so the back button does not walk back through the same page with
   * and without the parameter, and so a refresh does not re-scroll and re-open
   * a row the operator has since collapsed.
   */
  const resolveFocus = useCallback(() => {
    setIsPending(false);
    if (!focusId) return;

    const params = new URLSearchParams(location.search);
    params.delete("focus");
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}`, {
      replace: true,
    });
  }, [focusId, location.pathname, location.search, navigate]);

  const missFocus = useCallback(() => {
    setIsPending(false);
    setNotFound(true);
  }, []);

  /**
   * Clears the notice and the parameter together.
   *
   * Leaving `?focus=` in the URL after a dismissal means a refresh brings the
   * banner straight back, which reads as a bug.
   */
  const dismissFocus = useCallback(() => {
    setNotFound(false);
    resolveFocus();
  }, [resolveFocus]);

  return {
    focusId,
    isPending,
    resolveFocus,
    missFocus,
    notFound,
    dismissFocus,
  };
};

export default useFocusedRecord;

export interface FocusedRow {
  /** The id of the row to ring, or null. */
  highlighted: string | null;
  /** True when the page has looked and the record is not here. */
  notFound: boolean;
  /** Dismisses the not-found notice. */
  dismissFocus: () => void;
  /** `true` for the row to ring — pass to `focusRowClass`/`scrollRowIntoView`. */
  isFocused: (id: string) => boolean;
  /**
   * Drops the ring.
   *
   * The marker outlives `?focus=` deliberately, so it survives the URL tidy-up.
   * Pages clear it on the operator's next interaction rather than on a timer —
   * someone who has not looked away should not lose their place.
   */
  clearHighlight: () => void;
}

/**
 * The `?focus=` wiring every list page needs, in one place.
 *
 * Each page was otherwise repeating the same dozen lines: wait for the fetch,
 * look for the id in the loaded page, highlight it or report the miss, then
 * strip the parameter. Six copies of that is six chances to get the
 * dependency array wrong.
 *
 * `onFound` is for pages that do more than highlight — complaints expands the
 * row, for instance. It runs once, when the record is first located.
 */
export const useFocusedRow = <T extends { _id: string }>(
  items: T[] | undefined,
  isLoading: boolean,
  onFound?: (id: string) => void
): FocusedRow => {
  const { focusId, isPending, resolveFocus, missFocus, notFound, dismissFocus } =
    useFocusedRecord();
  const [highlighted, setHighlighted] = useState<string | null>(null);

  // Held in a ref so a caller passing an inline arrow does not re-run this.
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;

  useEffect(() => {
    // `isPending` gates on the current id, so this settles once per focus
    // rather than on every refetch.
    if (!focusId || !isPending || isLoading) return;

    if (items?.some((item) => item._id === focusId)) {
      setHighlighted(focusId);
      onFoundRef.current?.(focusId);
      resolveFocus();
    } else {
      missFocus();
    }
  }, [focusId, isPending, isLoading, items, resolveFocus, missFocus]);

  return {
    highlighted,
    notFound,
    dismissFocus,
    isFocused: (id: string) => highlighted === id,
    clearHighlight: () => setHighlighted(null),
  };
};
